import { Grid, TextField, Typography } from '@material-ui/core';
import { equals } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { CONSTANTS } from '../../../core/config/constants';
import { RangeOflong } from '../../../generated/model/rangeOflong';
import { useOnChange } from '../../../hooks/useOnChange';
import InputRangeSlider from '../RangeSlider';

export const allowedYearRange = {
  from: CONSTANTS.MINIMUM_SEARCHEABLE_YEAR,
  to: new Date().getFullYear(),
};

export const allowedReportYearRange = {
  from: new Date().getFullYear() - 4,
  to: new Date().getFullYear() - 2,
};

interface OwnProps {
  root?: boolean;
  field?: string;
  onChange: (state: RangeOflong) => void;
  defaultAllowedYearRange?: typeof allowedYearRange;
  defaultValue?: RangeOflong;
}

export default function YearRange({
  root,
  field,
  onChange,
  defaultAllowedYearRange = allowedYearRange,
  defaultValue = defaultAllowedYearRange,
}: OwnProps) {
  const [state, setState] = useState<RangeOflong>(defaultValue);
  const [inputMinState, setInputMinState] = useState<number | undefined>(state?.from);
  const [inputMaxState, setInputMaxState] = useState<number | undefined>(state?.to);
  const TextInput = useMemo(
    () => (props: any) => <TextField margin="normal" variant="outlined" fullWidth {...props} />,
    [],
  );
  useOnChange(onChange, state, root);

  useEffect(() => {
    if (!equals(defaultValue, state)) {
      setState(defaultValue);
      setInputMinState(defaultValue?.from);
      setInputMaxState(defaultValue?.to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  function handleInputChangeMin() {
    let from = inputMinState;

    if (
      typeof inputMinState === 'undefined' ||
      inputMinState < defaultAllowedYearRange.from ||
      (typeof inputMaxState !== 'undefined' && inputMinState > inputMaxState)
    ) {
      from = defaultAllowedYearRange.from;
    }

    setInputMinState(from);
    setState({ ...state, from });
  }

  function handleInputChangeMax() {
    let to = inputMaxState;

    if (
      typeof inputMaxState === 'undefined' ||
      inputMaxState > defaultAllowedYearRange.to ||
      (typeof inputMinState !== 'undefined' && inputMaxState < inputMinState)
    ) {
      to = defaultAllowedYearRange.to;
    }

    setInputMaxState(to);
    setState({ ...state, to });
  }

  return (
    <Grid container direction="column" id={field}>
      <Grid container direction="row" justify="space-between" alignItems="center">
        <Grid item xs={5}>
          <NumberFormat
            decimalScale={0} // disable floating number
            allowNegative={false} // disable negative number
            value={inputMinState}
            onValueChange={({ floatValue }) => setInputMinState(floatValue)}
            onBlur={handleInputChangeMin}
            onKeyPress={(event) => {
              if (CONSTANTS.ENTER_KEY_CODE === event.key) {
                handleInputChangeMin();
              }
            }}
            customInput={TextInput}
          />
        </Grid>
        <Grid>
          <Typography>-</Typography>
        </Grid>
        <Grid item xs={5}>
          <NumberFormat
            decimalScale={0} // disable floating number
            allowNegative={false} // disable negative number
            value={inputMaxState}
            onValueChange={({ floatValue }) => setInputMaxState(floatValue)}
            onBlur={handleInputChangeMax}
            onKeyPress={(event) => {
              if (CONSTANTS.ENTER_KEY_CODE === event.key) {
                handleInputChangeMax();
              }
            }}
            customInput={TextInput}
          />
        </Grid>
      </Grid>
      <InputRangeSlider
        onChange={(value) => {
          setState({ ...state, from: value[0], to: value[1] });
          if (value[0] !== inputMinState) {
            setInputMinState(value[0]);
          }
          if (value[1] !== inputMaxState) {
            setInputMaxState(value[1]);
          }
        }}
        minAllowed={defaultAllowedYearRange?.from}
        maxAllowed={defaultAllowedYearRange?.to}
        value={[state!!.from!!, state!!.to!!]}
      />
    </Grid>
  );
}
